import { MapPin, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useJobManagementManager } from "../controllers/JobManagementController";
import ApplicationManagement from "./ApplicationManagement";
import JobStatsCard from "../Components/JobStatsCard";
import { useJobsListViewModel } from "../viewmodel/job.viewmodel";

const JobDetails = () => {
  const { id } = useParams<{ id: string }>();

  const { state, fetchJobDetails, fetchApplicationStatsByStatus } =
    useJobsListViewModel();
  const jobDetailsData = state.jobDetails;
  const skills = jobDetailsData?.skills;
  const location = useLocation();
  useEffect(() => {
    if (id) {
      fetchJobDetails(id);
      fetchApplicationStatsByStatus(id);
    }
  }, [id]);
  const selectedJob = location.state as any;
  const source = selectedJob?.source;

  const formatEnum = (value?: string | null) =>
    value
      ? value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      : "N/A";

  const formatDate = (value?: string | null) => {
    if (!value) return "N/A";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const hasValue = (value?: string | number | null) =>
    value !== undefined && value !== null && String(value).trim() !== "";

  const jobValue = (field: string) =>
    (jobDetailsData as any)?.[field] ?? selectedJob?.[field];

  const renderTextBlock = (value?: string | null) =>
    hasValue(value) ? (
      <p className="text-sm text-gray-700 whitespace-pre-line">{value}</p>
    ) : (
      <p className="text-sm text-gray-400 italic text-center">
        No data found
      </p>
    );

  const detailRows: Array<[string, string | number | null | undefined]> = [
    ["Recruiter", jobValue("recruiter_name")],
    ["Hiring Manager", jobValue("hiring_manager_name")],
    ["Created By", jobValue("created_by_name")],
    ["Status", formatEnum(jobValue("status"))],
    ["Job Type", formatEnum(jobValue("job_type"))],
    ["Work Mode", formatEnum(jobValue("work_mode"))],
    ["Experience Level", formatEnum(jobValue("experience_level"))],
    ["Education Level", formatEnum(jobValue("education_level"))],
    ["Job Function", formatEnum(jobValue("job_function"))],
    ["Location", jobValue("location")],
    [
      "Salary",
      hasValue(jobValue("salary_range"))
        ? `${jobValue("salary_currency") || ""} ${jobValue("salary_range")}`
        : `${jobValue("salary_currency") || ""} ${jobValue("salary_min") ?? "N/A"} - ${jobValue("salary_max") ?? "N/A"}`,
    ],
    ["Vacancies", jobValue("vacancies")],
    ["Max Applications", jobValue("max_applications")],
    ["Posting Visibility", formatEnum(jobValue("posting_visibility"))],
    ["Approval Flow", jobValue("approval_flow_name")],
    ...(jobValue("visa_sponsorship") === true
      ? ([["Visa Sponsorship", "Yes"]] as Array<[string, string]>)
      : []),
    ...(jobValue("relocation_offered") === true
      ? ([["Relocation Offered", "Yes"]] as Array<[string, string]>)
      : []),
    ["Travel", `${jobValue("travel_percentage") ?? 0}%`],
    ["Hiring Urgency", formatEnum(jobValue("hiring_urgency"))],
    ["Target Start Date", formatDate(jobValue("target_start_date"))],
    ["Application Deadline", formatDate(jobValue("application_deadline"))],
    ["Created At", formatDate(jobValue("created_at"))],
    ["Published At", formatDate(jobValue("published_at"))],
    ["Interview Process", jobValue("interview_process")],
    ["Approval Request", selectedJob?.request_id],
    [
      "Approval Level",
      selectedJob?.current_level && selectedJob?.total_levels
        ? `${selectedJob.current_level} of ${selectedJob.total_levels}`
        : undefined,
    ],
    ["Submitted For Approval", formatDate(selectedJob?.submitted_at)],
  ];
  const [activeTab, setActiveTab] = useState<
    | "description"
    | "requirements"
    | "benefits"
    | "skills"
    | "other"
    | "applications"
  >("description");
  const tabs = [
    { id: "description", name: "Job Description", icon: "" },
    { id: "requirements", name: "Requirements", icon: "" },
    { id: "benefits", name: "Benefits", icon: "" },
    { id: "skills", name: "Skills", icon: "" },
    { id: "other", name: "Other Info", icon: "" },
    { id: "applications", name: "Applications", icon: "" },
  ];
  if (state.loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to={source === "pending" ? "/company/jobs" : "/company/jobs"}
              state={
                source === "pending"
                  ? { tab: "pendingApprovals" }
                  : { tab: "jobs" }
              }
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-indigo-600"
            >
              {source === "pending" ? "Pending Jobs" : "Jobs"}
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                {selectedJob?.title || jobDetailsData?.title}
              </span>
            </div>
          </li>
        </ol>
      </nav>
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-shrink-0 h-20 w-20">
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-2xl">
                  {(jobDetailsData?.title || selectedJob?.title || "NA")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
            </div>
            <div className="sm:ml-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedJob?.title || jobDetailsData?.title || ""}
              </h1>
              <p className="text-lg text-gray-600 mt-1">
                <span className="font-semibold">
                  {selectedJob?.companyName}
                </span>
                {/* {candidate.current_job_title} {candidate.current_company && `at ${candidate.current_company}`} */}
              </p>
              <div className="flex flex-wrap items-center mt-2 gap-4">
                {(selectedJob?.location || jobDetailsData?.location) && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm text-gray-500">
                      {selectedJob?.location || jobDetailsData?.location}
                    </span>
                  </div>
                )}
                {(selectedJob?.experience || jobDetailsData?.experience_level) && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm text-gray-500">
                      {selectedJob?.experience
                        ? `${selectedJob.experience} years experience`
                        : formatEnum(jobDetailsData?.experience_level)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <JobStatsCard
          title="Total Applications"
          value={state.applicationStats?.total ?? 0}
          icon={
            <svg
              className="w-5 h-5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        {/* {applications.filter(app => app.status === 'reviewing').length} */}
        <JobStatsCard
          title="Under Review"
          value={state.applicationStats?.by_status.in_review ?? 0}
          icon={
            <svg
              className="w-5 h-5 text-yellow-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path
                fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        {/* {applications.filter(app => app.status === 'interview_scheduled').length} */}
        <JobStatsCard
          title="Interviews Scheduled"
          value={state.applicationStats?.by_status.interview_scheduled ?? 0}
          icon={
            <svg
              className="w-5 h-5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        {/* {applications.filter(app => app.status === 'hired').length} */}
        <JobStatsCard
          title="Hired"
          value={state.applicationStats?.by_status.hired ?? 0}
          icon={
            <svg
              className="w-5 h-5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex overflow-x-auto px-4 sm:px-6 gap-6 sm:gap-8 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-6">
          {activeTab === "description" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  {" "}
                  Job Description
                </h3>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                {renderTextBlock(jobValue("description"))}
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Responsibilities
                </h3>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                {renderTextBlock(jobValue("responsibilities"))}
              </div>
            </div>
          )}

          {activeTab === "requirements" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Requirements
                </h3>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <ul className="space-y-3">
                  {renderTextBlock(jobValue("requirements"))}

                  {/* {jobDetailsData?..map((item, index) => (
              <li key={index} className="flex gap-3">
                      <span className="text-primary ">•</span>
                      <p className="text-sm text-gray-700 mb-3">{item}</p>
                    </li>
                 ))} */}
                </ul>
              </div>
            </div>
          )}
          {activeTab === "benefits" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  {" "}
                  Benefits
                </h3>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="grid sm:grid-cols-2 gap-3">
                  {renderTextBlock(jobValue("benefits"))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "skills" && (
            <div className="space-y-6">
              {skills && skills.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Skills
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {skills.map((skill, index) => (
                      <div
                        key={index}
                        className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-indigo-900">
                            {skill.skill_name}
                          </h4>
                        </div>
                        <div className="space-y-1">
                          {skill.proficiency_level && (
                            <p className="text-xs text-indigo-700">
                              <span className="font-medium">Level:</span>{" "}
                              {skill.proficiency_level}
                            </p>
                          )}
                          {skill.minimum_years && (
                            <p className="text-xs text-indigo-700">
                              <span className="font-medium">Experience:</span>{" "}
                              {skill.minimum_years} years
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-6">
                  <span className="text-sm text-gray-400">
                    No skills available
                  </span>
                </div>
              )}
            </div>
          )}
          {activeTab === "other" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Job Details
                </h3>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {detailRows.map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-md border border-gray-200 bg-white p-3"
                    >
                      <div className="text-xs font-medium text-gray-500">
                        {label}
                      </div>
                      <div className="mt-1 font-semibold text-gray-900 break-words whitespace-pre-line">
                        {hasValue(value) ? value : "N/A"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeTab === "applications" && (
            <ApplicationManagement jobId={jobDetailsData?.id || selectedJob?.id || ""} />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
